import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { LocalizationContext, LocalizationContextBuilder } from '@amzn/arb-tools';
import { MbmProvider } from '@amzn/react-arb-tools';
import { useUserPreferencesQuery } from 'src/data/api/orbit-service-ts/user-preferences/queries';
import { APP_PROGRAM_ID, useMidway, useServiceSessions } from '../auth';
import { createMobileMbm } from './createMobileMbm';
import { DEFAULT_LOCALE } from './locales';

interface MobileI18nProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Fallback context used before {@link MobileI18nProvider} mounts (in
 * unit tests, Storybook, or any island that renders `useMessages` in
 * isolation). Baked to {@link DEFAULT_LOCALE} so untranslated bundles
 * render the source strings instead of erroring.
 */
const FALLBACK_LOCALIZATION_CONTEXT = new LocalizationContextBuilder()
  .withLocale(DEFAULT_LOCALE)
  .withDefaultLocale(DEFAULT_LOCALE)
  .build();

/**
 * Publishes the authenticated user's resolved LocalizationContext to
 * `useMessages`. We use our OWN context (not `@amzn/react-arb-tools`'s
 * `LocalizationContextContext`) because that library's context is only
 * wired when `MbmProvider` gets the option-shape props — passing a
 * pre-built `mbm` short-circuits the wiring and locks the locale to
 * whatever the mbm's `localizationContextProvider` returns. `useMessages`
 * threads this context into `useBundle`'s second argument, which
 * `mbm.getBundle` uses in preference to its baked-in callback.
 */
const MobileLocalizationContext = createContext<LocalizationContext>(
  FALLBACK_LOCALIZATION_CONTEXT,
);

/**
 * Read the resolved LocalizationContext for the mobile shell. Used by
 * `useMessages` to force arb-tools to load bundles for the current
 * user's locale.
 */
export function useMobileLocalizationContext(): LocalizationContext {
  return useContext(MobileLocalizationContext);
}

/**
 * Localization provider for the v2 mobile shell.
 *
 * Owns the i18n stack: builds the AsyncMessageBundleManager once (its
 * resource pack is inlined at build time, no runtime fetch) and
 * publishes a `LocalizationContext` that tracks the authenticated
 * user's preferred language.
 *
 * Locale propagation to arb-tools:
 *   MobileI18nProvider builds a LocalizationContext with the user's
 *   preferred language and exposes it via {@link MobileLocalizationContext}.
 *   `useMessages` reads it and passes it as the second argument to
 *   `useBundle`, which places it in the effect's dep array and threads
 *   it into `mbm.getBundle(bundle, context)`. arb-tools' getBundle
 *   prefers the explicit context over the mbm's internal callback, so
 *   the runtime locale ships regardless of how the mbm was built.
 *
 * Why this lives above `UserProvider` rather than inside it: keeps the
 * i18n update loop independent of the rest of the user-context query
 * chain. To get the user's language without depending on
 * `UserProvider`, this component runs the same Midway + service-session
 * + preferences fetch itself. React Query dedupes the request so the
 * prefs hook below in `UserProvider` reuses the cached response.
 */
export function MobileI18nProvider({
  children,
}: MobileI18nProviderProps): React.ReactElement {
  const { userAlias: alias } = useMidway();
  const { isReady: areSessionsReady } = useServiceSessions(alias);

  // Same query, same key, same enabled-gate as inside UserProvider — React
  // Query coalesces the two consumers into a single network call.
  const { data: preferences } = useUserPreferencesQuery(
    { userId: alias, programId: APP_PROGRAM_ID },
    { enabled: areSessionsReady },
  );

  const locale = preferences?.profileSettings?.language ?? DEFAULT_LOCALE;

  // Mirror the resolved locale onto `<html lang="...">`. Screen readers
  // and browser features (spell-check, translate hints) key their
  // language handling off this attribute, and it's a runtime signal we
  // can inspect in dev tools to confirm the i18n pipeline is seeing the
  // user's preference — the static `en-US` from `public/index.html`
  // stays put until this effect runs.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const mbm = useMemo(() => createMobileMbm(), []);

  // Rebuild the context whenever the locale changes so `useMessages`
  // consumers get a fresh dep-array value and arb-tools refetches
  // bundles for the new locale.
  const localizationContext = useMemo(
    () =>
      new LocalizationContextBuilder()
        .withLocale(locale)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build(),
    [locale],
  );

  return (
    <MbmProvider mbm={mbm} defaultLocalizationContext={localizationContext}>
      <MobileLocalizationContext.Provider value={localizationContext}>
        {children}
      </MobileLocalizationContext.Provider>
    </MbmProvider>
  );
}
